import { apiFetch, urlDelApi } from "@/lib/api";
import type { SeccionInforme } from "@/lib/informes";

/* ---------------------------------------------------------------------------
   Registro de terreno: lo que el encargado sincronizó desde la App de terreno
   y el API compone en `GET /api/informes/{informe}/terreno`.

   Son tipos del CONTRATO (`odd/tasks/registro-de-terreno.md`): la forma la
   decide el servidor (`VistaDelRegistroDeTerreno`) y esta pestaña solo la lee.
   Nada de lo que viaja acá se escribe de vuelta.
--------------------------------------------------------------------------- */

export interface ArchivoDeTerreno {
  hash: string;
  mime: string;
  bytes: number;
}

export interface TextoDeTerreno {
  uuid: string;
  texto: string;
  capturado_en: string | null;
  sincronizado_en: string | null;
  /**
   * La sincronización de la App en que llegó (uuid). `null` es una captura
   * anterior a las sincronizaciones: nunca es NUEVA ni se resalta, y solo se ve
   * bajo "Todas". Ausente en los datos que no la traen.
   */
  sincronizacion_id?: string | null;
}

export interface AudioDeTerreno {
  uuid: string;
  duracion_ms: number | null;
  capturado_en: string | null;
  sincronizado_en: string | null;
  /**
   * La sincronización de la App en que llegó (uuid). `null` es una captura
   * anterior a las sincronizaciones: nunca es NUEVA ni se resalta, y solo se ve
   * bajo "Todas". Ausente en los datos que no la traen.
   */
  sincronizacion_id?: string | null;
  archivo: ArchivoDeTerreno | null;
  /** El texto, solo cuando `transcripcion_estado` es `lista`; `null` en cualquier otro caso. */
  transcripcion: string | null;
  /**
   * `null` es una nota anterior a la transcripción: nadie la intentó, y no es
   * lo mismo que `pendiente` (que hay un trabajo en curso).
   */
  transcripcion_estado: EstadoDeTranscripcionDeTerreno | null;
  /** Solo con `fallida`: el código del motivo, nunca un mensaje del proveedor. */
  transcripcion_motivo: string | null;
}

export type EstadoDeTranscripcionDeTerreno = "pendiente" | "lista" | "fallida";

export interface FotoDeTerreno {
  uuid: string;
  capturado_en: string | null;
  sincronizado_en: string | null;
  /**
   * La sincronización de la App en que llegó (uuid). `null` es una captura
   * anterior a las sincronizaciones: nunca es NUEVA ni se resalta, y solo se ve
   * bajo "Todas". Ausente en los datos que no la traen.
   */
  sincronizacion_id?: string | null;
  archivo: ArchivoDeTerreno | null;
  /**
   * Dónde se tomó, si la app lo registró. Las dos o ninguna: una foto de la
   * galería, o tomada sin permiso de ubicación, viene en `null`.
   */
  latitud: number | null;
  longitud: number | null;
}

export interface GpsDeTerreno {
  uuid: string;
  latitud: number | null;
  longitud: number | null;
  precision_m: number | null;
  capturado_en: string | null;
  sincronizado_en: string | null;
  /**
   * La sincronización de la App en que llegó (uuid). `null` es una captura
   * anterior a las sincronizaciones: nunca es NUEVA ni se resalta, y solo se ve
   * bajo "Todas". Ausente en los datos que no la traen.
   */
  sincronizacion_id?: string | null;
}

/**
 * Dónde quedó una captura rápida y quién la puso ahí. `origen` `app` es lo que
 * el encargado asignó en terreno (la web no lo toca); `web` es lo que se
 * clasificó desde la pestaña (se puede cambiar o devolver); `null`, sin
 * clasificar.
 */
export interface ClasificacionDeCaptura {
  destino: string | null;
  item_ref: string | null;
  origen: "app" | "web" | null;
  destino_nombre: string | null;
  clasificada_en: string | null;
  clasificada_por: string | null;
}

export interface RegistroDeTerreno {
  uuid: string;
  capturado_en: string | null;
  sincronizado_en: string | null;
  /**
   * La sincronización de la App en que llegó (uuid). `null` es una captura
   * anterior a las sincronizaciones: nunca es NUEVA ni se resalta, y solo se ve
   * bajo "Todas". Ausente en los datos que no la traen.
   */
  sincronizacion_id?: string | null;
  /** Compatibilidad: la primera de `fotos`. Usá `fotosDelRegistro()`. */
  foto: FotoDeTerreno | null;
  /**
   * TODAS las fotos del registro (la propia y las hijas), en orden. Opcional
   * porque un API anterior no la manda: léela con `fotosDelRegistro()`.
   */
  fotos?: FotoDeTerreno[];
  texto: string | null;
  audios: AudioDeTerreno[];
  /** Las de su foto; `null` si no tiene foto o la foto no trae ubicación. */
  latitud: number | null;
  longitud: number | null;
  /** Solo en las capturas rápidas; ausente en los registros que cuelgan de un ítem por su cuenta. */
  clasificacion?: ClasificacionDeCaptura;
}

export interface CampoDeTerreno {
  clave: string;
  nombre: string;
  textos: TextoDeTerreno[];
  audios: AudioDeTerreno[];
  fotos: FotoDeTerreno[];
  gps: GpsDeTerreno | null;
  portada: FotoDeTerreno | null;
}

/** Los cuatro datos de una construcción que admiten nota desde la App de terreno. */
export type CampoDeDatoDeConstruccion = "tipo" | "anio" | "superficie" | "clase";

export interface NotaDeDatoDeTerreno {
  texto: string;
  capturado_en: string | null;
  sincronizacion_id?: string | null;
}

export type NotasDeUnaConstruccion = Partial<
  Record<CampoDeDatoDeConstruccion, NotaDeDatoDeTerreno>
>;

export interface ItemDeTerreno {
  referencia: string;
  nombre: string;
  nuevo: boolean;
  datos: { etiqueta: string; valor: string }[];
  registros: RegistroDeTerreno[];
  /**
   * Solo en las construcciones (siempre un objeto, a veces vacío); ausente en
   * las demás secciones. Es una PROPUESTA de quien estuvo en terreno para quien
   * redacta el informe: nunca cambia el valor del dato.
   */
  notas?: NotasDeUnaConstruccion;
  /**
   * Solo en la maquinaria (ausentes en las demás secciones). El año y las
   * unidades llegan ESTRUCTURADOS para precargar la fila nueva; `ausente` es la
   * observación "No está en el predio" del encargado: informa y nunca edita.
   */
  anio_fabricacion?: number | null;
  unidades?: number | null;
  ausente?: boolean;
  /**
   * Cuándo capturó el encargado la observación de ausencia que gana (ISO), o
   * `null`. La web la compara con el «Mantener» de la fila: una observación más
   * nueva vuelve a mostrar el aviso.
   */
  ausente_desde?: string | null;
  /**
   * Solo en las plantaciones (`plantacion:<id>` y `plantacion-terreno:<uuid>`):
   * los nueve campos de la fila, ESTRUCTURADOS, para precargar la fila nueva.
   * `superficie_ha` llega como número (la fila guardada lo trae como string).
   */
  especie?: string | null;
  variedad?: string | null;
  anio_plantacion?: number | null;
  superficie_ha?: number | null;
  cuartel?: string | null;
  portainjerto?: string | null;
  distancia_plantacion?: string | null;
  plantas_por_ha?: number | null;
  sistema_riego?: string | null;

  /**
   * Solo en las instalaciones y obras: llegan ESTRUCTURADOS (como el año y las
   * unidades de la maquinaria) para precargar la fila nueva; `ausente` es el
   * mismo de arriba.
   */
  anio?: number | null;
  cantidad?: number | null;
  valor_reposicion_uf_un?: number | null;
  valor_comercial_uf_un?: number | null;

  /**
   * Solo en las carpetas de «Otros antecedentes» (`carpeta-terreno:<uuid>`): lo
   * que el encargado anotó de qué contiene, o `null`. Su presencia es lo que
   * distingue una carpeta de cualquier otro ítem. Carpetas solo existen en
   * terreno: no hay tabla web ni nada que validar.
   */
  contenido?: string | null;

  /**
   * Solo en «Roles del informe» (sección `antecedentes_generales`: `rol:<id>` y
   * `rol-terreno:<uuid>`). `rol` son los cinco campos del rol, ESTRUCTURADOS
   * (superficies como número); `propuesta` es lo que el encargado editó sobre un
   * rol de la web (la captura más reciente) o `null`. Son OBSERVACIONES: la web
   * solo las ofrece, nunca escribe el rol por su cuenta.
   */
  rol?: DatosDeRolDeTerreno;
  propuesta?: DatosDeRolDeTerreno | null;
  propuesta_capturada_en?: string | null;
  propuesta_por?: string | null;
}

export interface DatosDeRolDeTerreno {
  rol_sii: string | null;
  comuna_codigo: string | null;
  comuna: string | null;
  /** Hectáreas. */
  superficie: number | null;
  /** Metros cuadrados. */
  superficie_construida: number | null;
}

export interface SeccionDeTerreno {
  clave: string;
  nombre: string;
  /**
   * La pestaña del editor donde vive lo que esta sección describe, o `null`.
   * `otros` es la sección de las carpetas de terreno: no tiene pestaña web.
   */
  pestana: SeccionInforme | "otros" | null;
  campos: CampoDeTerreno[];
  items_titulo: string | null;
  items: ItemDeTerreno[];
  capturas: RegistroDeTerreno[];
  /**
   * Solo en «sin clasificar»: las capturas que la web ya clasificó. NO cuentan
   * en `capturas` (que son las que faltan) y además aparecen en su destino.
   */
  clasificadas_en_la_web?: RegistroDeTerreno[];
}

/**
 * Las notas generales del PROYECTO: un solo texto y todos los audios que el
 * encargado quiso, fuera de cualquier sección. Siempre vienen en la vista (vacías
 * si no hay nada). `revisadas` ya trae resuelta la regla "material nuevo reabre":
 * la web no la recalcula.
 */
export interface NotasGeneralesDeTerreno {
  texto: TextoDeTerreno | null;
  audios: AudioDeTerreno[];
  revisadas: boolean;
  revisadas_en: string | null;
}

/** Una corrida de la App: lo que el encargado mandó de una vez. Las más antiguas primero. */
export interface SincronizacionDeTerreno {
  id: string;
  /** Cuándo llegó la primera captura (ISO). */
  en: string;
  capturas: number;
  /** Llegó después de la última que ESTE usuario marcó como revisada. */
  nueva: boolean;
}

export interface RegistroDeTerrenoDelInforme {
  encargado: { id: number; nombre: string } | null;
  visita: { desde: string; hasta: string } | null;
  ultima_sincronizacion: string | null;
  /** Solo las que traen al menos una captura. */
  sincronizaciones: SincronizacionDeTerreno[];
  /** La última que el usuario actual marcó como revisada, o `null`. */
  sincronizacion_revisada: string | null;
  totales: { fotos: number; audios: number; textos: number };
  notas_generales: NotasGeneralesDeTerreno;
  secciones: SeccionDeTerreno[];
  /**
   * Si quien mira puede descartar, restaurar y vaciar (el ejecutivo del informe
   * o el Super Admin; viene de `meta.puede_descartar`). Falso: la web no ofrece
   * ninguno de los tres, porque el servidor respondería 403. Ausente equivale a
   * falso (`fetchRegistroDeTerreno` siempre lo fija).
   */
  puede_descartar?: boolean;
}

export async function fetchRegistroDeTerreno(
  informeId: number,
): Promise<RegistroDeTerrenoDelInforme> {
  const respuesta = await apiFetch<{
    data: RegistroDeTerrenoDelInforme;
    meta?: { puede_descartar?: boolean };
  }>(`/api/informes/${informeId}/terreno`);

  return { ...respuesta.data, puede_descartar: respuesta.meta?.puede_descartar === true };
}

/**
 * "Marcar como revisado": guarda hasta qué sincronización vio ESTE usuario y
 * devuelve la vista fresca (la misma de `fetchRegistroDeTerreno`).
 */
export async function marcarSincronizacionRevisada(
  informeId: number,
  sincronizacionId: string,
): Promise<RegistroDeTerrenoDelInforme> {
  const respuesta = await apiFetch<{ data: RegistroDeTerrenoDelInforme }>(
    `/api/informes/${informeId}/terreno/revision`,
    { method: "PUT", body: JSON.stringify({ sincronizacion_id: sincronizacionId }) },
  );

  return respuesta.data;
}

/**
 * La url de los bytes de un registro (foto o audio).
 *
 * `?v=<hash>` no cambia lo que el servidor responde: es la llave de la caché del
 * navegador. El `ETag` del endpoint ya es el hash, así que un archivo que se
 * reemplaza por otro estrena url y nunca se sirve el viejo.
 */
export function urlDelArchivoDeTerreno(informeId: number, uuid: string, hash: string): string {
  return urlDelApi(`/api/informes/${informeId}/terreno/registros/${uuid}/archivo?v=${hash}`);
}

/* ---------------------------------------------------------------------------
   Papelera de terreno (`odd/tasks/papelera-terreno.md`).

   Descartar, restaurar y vaciar son actos MANUALES de quien tasa; nada ocurre
   solo. Descartar y restaurar se deshacen; vaciar es el único irreversible: el
   servidor borra los archivos.
--------------------------------------------------------------------------- */

/** Un bloque descartado, tal como lo lista `GET /api/informes/{i}/terreno/papelera`. */
export interface RegistroEnPapeleraDeTerreno {
  uuid: string;
  /** El tipo crudo del servidor: `nota_de_voz`, `foto_de_construccion`, `captura_rapida`… */
  tipo: string;
  referencia: string | null;
  datos: Record<string, unknown>;
  texto: string | null;
  archivo: ArchivoDeTerreno | null;
  transcripcion: string | null;
  capturado_en: string | null;
  descartado_en: string | null;
  descartado_por: string | null;
  /** Quién lo capturó en terreno. */
  usuario: string | null;
  /** Cuántos bloques asociados se fueron con este (fotos y audios que cuelgan de él). */
  hijos: number;
}

export interface PapeleraDeTerreno {
  registros: RegistroEnPapeleraDeTerreno[];
  total: number;
  /** Ver {@link RegistroDeTerrenoDelInforme.puede_descartar}: sin él la papelera solo se mira. */
  puede_descartar: boolean;
}

const rutaDelRegistro = (informeId: number, uuid: string) =>
  `/api/informes/${informeId}/terreno/registros/${uuid}`;

/** Manda un bloque a la papelera. Sus fotos y audios asociados se esconden con él. */
export async function descartarRegistroDeTerreno(informeId: number, uuid: string): Promise<void> {
  await apiFetch(rutaDelRegistro(informeId, uuid), { method: "DELETE" });
}

export async function restaurarRegistroDeTerreno(informeId: number, uuid: string): Promise<void> {
  await apiFetch(`${rutaDelRegistro(informeId, uuid)}/restaurar`, { method: "POST" });
}

export async function fetchPapeleraDeTerreno(informeId: number): Promise<PapeleraDeTerreno> {
  const respuesta = await apiFetch<{
    data: RegistroEnPapeleraDeTerreno[];
    meta: { total: number; puede_descartar?: boolean };
  }>(`/api/informes/${informeId}/terreno/papelera`);

  return {
    registros: respuesta.data,
    total: respuesta.meta.total,
    puede_descartar: respuesta.meta.puede_descartar === true,
  };
}

/** IRREVERSIBLE: borra los archivos. Devuelve cuántos bloques eliminó. */
export async function vaciarPapeleraDeTerreno(informeId: number): Promise<number> {
  const respuesta = await apiFetch<{ meta: { eliminados: number } }>(
    `/api/informes/${informeId}/terreno/papelera`,
    { method: "DELETE" },
  );

  return respuesta.meta.eliminados;
}

/** El destino que la web le da a una captura: una sección y, opcionalmente, un ítem, campo o punto. */
export interface DestinoDeCaptura {
  destino: string;
  item_ref: string | null;
}

export async function clasificarCaptura(
  informeId: number,
  uuid: string,
  destino: DestinoDeCaptura,
): Promise<RegistroDeTerreno> {
  const respuesta = await apiFetch<{ data: RegistroDeTerreno }>(
    `/api/informes/${informeId}/terreno/capturas/${uuid}/clasificacion`,
    { method: "PUT", body: JSON.stringify(destino) },
  );

  return respuesta.data;
}

/** «Devolver a sin clasificar»: quita la clasificación que puso la web. */
export async function devolverCapturaASinClasificar(
  informeId: number,
  uuid: string,
): Promise<RegistroDeTerreno> {
  const respuesta = await apiFetch<{ data: RegistroDeTerreno }>(
    `/api/informes/${informeId}/terreno/capturas/${uuid}/clasificacion`,
    { method: "DELETE" },
  );

  return respuesta.data;
}

/**
 * Marca o desmarca «Revisadas» en las notas generales. Junto con la clasificación de
 * capturas, es lo único que la web escribe del registro de terreno.
 */
export async function revisarNotasGenerales(
  informeId: number,
  revisadas: boolean,
): Promise<NotasGeneralesDeTerreno> {
  const respuesta = await apiFetch<{ data: NotasGeneralesDeTerreno }>(
    `/api/informes/${informeId}/terreno/notas-generales/revision`,
    { method: "PUT", body: JSON.stringify({ revisadas }) },
  );

  return respuesta.data;
}
